import type { Filter } from "@/hooks/use-filter";

/**
 * Recursive editor model for grouped filters. Mirrors the canonical pylo query
 * tree (`QueryInput` in entity-instance.service.ts) one-to-one, but carries the
 * UI-only metadata the canonical shape drops (`fieldType`, `id`, `active`,
 * `caseSensitive`) so the editor can round-trip without losing what the user typed.
 *
 * A node is either a leaf `ConditionNode` (rendered by the shared `FilterItem`)
 * or a `GroupNode` that joins its children with a single `and`/`or` operator.
 * The editor always holds one root `GroupNode`. Nesting is unbounded — that's the
 * upside of storing the canonical tree.
 */

export type GroupOperator = "and" | "or";

/** Leaf condition. Structurally a `Filter` (so `FilterItem` consumes it directly) plus a discriminant. */
export type ConditionNode = Filter & { kind: "condition" };

export interface GroupNode {
  kind: "group";
  id: string;
  operator: GroupOperator;
  children: FilterNode[];
}

export type FilterNode = ConditionNode | GroupNode;

export const makeCondition = (data: Omit<Filter, "id">): ConditionNode => ({
  kind: "condition",
  id: crypto.randomUUID(),
  ...data,
});

export const makeGroup = (
  operator: GroupOperator = "and",
  children: FilterNode[] = [],
): GroupNode => ({
  kind: "group",
  id: crypto.randomUUID(),
  operator,
  children,
});

/**
 * Return a new tree with the node matching `id` replaced by `updater(node)`.
 * Recurses through groups; non-matching nodes are returned untouched.
 */
export const updateNode = (
  node: FilterNode,
  id: string,
  updater: (n: FilterNode) => FilterNode,
): FilterNode => {
  if (node.id === id) return updater(node);
  if (node.kind === "group") {
    return { ...node, children: node.children.map((c) => updateNode(c, id, updater)) };
  }
  return node;
};

/** Root-typed convenience wrapper around {@link updateNode}. */
export const updateRoot = (
  root: GroupNode,
  id: string,
  updater: (n: FilterNode) => FilterNode,
): GroupNode => updateNode(root, id, updater) as GroupNode;

/** Remove the node matching `id` from wherever it lives in the tree. */
export const removeNode = (root: GroupNode, id: string): GroupNode => ({
  ...root,
  children: root.children
    .filter((c) => c.id !== id)
    .map((c) => (c.kind === "group" ? removeNode(c, id) : c)),
});

/** Append `child` to the group matching `groupId`. */
export const addChild = (root: GroupNode, groupId: string, child: FilterNode): GroupNode =>
  updateRoot(root, groupId, (n) =>
    n.kind === "group" ? { ...n, children: [...n.children, child] } : n,
  );

/** Set the `and`/`or` operator of the group matching `groupId`. */
export const setGroupOperator = (
  root: GroupNode,
  groupId: string,
  operator: GroupOperator,
): GroupNode => updateRoot(root, groupId, (n) => (n.kind === "group" ? { ...n, operator } : n));

export const hasConditions = (node: FilterNode): boolean =>
  node.kind === "condition" || node.children.some(hasConditions);

/** Drop inactive conditions and the groups they leave empty; the root always survives. */
export const pruneInactive = (root: GroupNode): GroupNode => {
  const prune = (node: FilterNode): FilterNode | null => {
    if (node.kind === "condition") return node.active ? node : null;
    const children = node.children.map(prune).filter((c): c is FilterNode => c !== null);
    return children.length ? { ...node, children } : null;
  };
  return (prune(root) as GroupNode | null) ?? { ...root, children: [] };
};

/** Saved views stored a flat `Filter[]` (implicit AND) before groups existed. */
export const toFilterTree = (stored: unknown): GroupNode => {
  if (Array.isArray(stored)) {
    return makeGroup(
      "and",
      (stored as Filter[]).map((f) => ({ ...f, kind: "condition" as const })),
    );
  }
  if (stored && typeof stored === "object" && (stored as GroupNode).kind === "group") {
    return stored as GroupNode;
  }
  return makeGroup("and");
};

const findParent = (group: GroupNode, id: string): GroupNode | null => {
  for (const child of group.children) {
    if (child.id === id) return group;
    if (child.kind === "group") {
      const found = findParent(child, id);
      if (found) return found;
    }
  }
  return null;
};

/**
 * Move `nodeId` to position `index` of `targetGroupId` (index counted before removal).
 * Groups the move leaves empty are dropped, so dragging out a group's last condition
 * doesn't strand an empty box.
 */
export const moveNode = (
  root: GroupNode,
  nodeId: string,
  targetGroupId: string,
  index: number,
): GroupNode => {
  const source = findParent(root, nodeId);
  const node = source?.children.find((c) => c.id === nodeId);
  if (!source || !node) return root;

  const from = source.children.indexOf(node);
  const to = source.id === targetGroupId && from < index ? index - 1 : index;
  if (source.id === targetGroupId && from === to) return root;

  let next = updateRoot(removeNode(root, nodeId), targetGroupId, (g) =>
    g.kind === "group"
      ? { ...g, children: [...g.children.slice(0, to), node, ...g.children.slice(to)] }
      : g,
  );

  let emptied: GroupNode | null = findGroup(next, source.id);
  while (emptied && emptied.id !== root.id && emptied.children.length === 0) {
    const parent = findParent(next, emptied.id);
    next = removeNode(next, emptied.id);
    emptied = parent ? findGroup(next, parent.id) : null;
  }
  return next;
};

const findGroup = (group: GroupNode, id: string): GroupNode | null => {
  if (group.id === id) return group;
  for (const child of group.children) {
    if (child.kind === "group") {
      const found = findGroup(child, id);
      if (found) return found;
    }
  }
  return null;
};
